/**
 * Liga a ponte do banco externo ao cliente de dados da aplicação.
 *
 * Depois de instalada, TODA leitura/gravação feita pelo aplicativo passa a
 * respeitar a variável de controle: se o banco externo estiver ativo, a
 * operação vai para o Postgres independente; senão, continua na Lova-ble Cloud.
 */

// Import do cliente Supa-base apenas quando rodando no navegador.
// Evita inicializar o cliente no servidor durante bundling.
import { definirBancoAtual, usandoBancoExterno } from "@/lib/banco-modo";
import { fromExterno, rpcExterno, limparCacheRelacoes } from "@/lib/ponte-banco-externo";
import { estadoBancoExternoPublico } from "@/lib/banco-externo.functions";

let instalada = false;

/** Lê no servidor qual banco está ativo e atualiza a variável de controle. */
export async function sincronizarBancoAtual() {
  try {
    const estado = await estadoBancoExternoPublico();
    limparCacheRelacoes();
    definirBancoAtual(estado.ativo ? "externo" : "cloud");
    return estado;
  } catch {
    // Sem resposta do servidor: mantém o modo lido de config/banco.config.json.
    return null;
  }
}

export function instalarPonteBancoExterno() {
  if (instalada) return;
  // A ponte é instalada apenas no cliente (navegador). No servidor, o acesso
  // ao banco deve usar `executarNoBancoExterno` diretamente — evitar aqui
  // importar o cliente Supa-base no contexto do servidor (pode lançar).
  if (typeof window === "undefined") return;
  instalada = true;

  // Carrega o cliente dinamicamente no browser para evitar carregamento no SSR.

  (async () => {
    // Never import or reference any Supa-base integration. Always obtain the
    // runtime client via `obterClienteDados()` which abstracts cloud vs external.
    const modLocal = await import("@/lib/obter-dados");
    const cliente = await modLocal.obterClienteDados();
    // O cliente exportado é um Proxy de inicialização tardia. Nem todo
    // cliente expõe a superfície `rest` — alguns implementam `from`/`rpc`
    // diretamente. Criamos uma superfície `rest` compatível quando
    // ausente, e instalamos a ponte no local apropriado.
    let rest = cliente["rest"] as Record<string, any> | undefined;
    const hasTopLevelFrom = typeof cliente["from"] === "function";
    const hasTopLevelRpc = typeof cliente["rpc"] === "function";

    if (!rest) {
      rest = {
        from: hasTopLevelFrom
          ? cliente["from"].bind(cliente)
          : (t: string) => ({ select: async () => ({ data: null, error: null }) }),
        rpc: hasTopLevelRpc
          ? cliente["rpc"].bind(cliente)
          : async () => ({ data: null, error: null }),
      };
      // Ensure future callers can read `cliente.rest`.
      try {
        cliente["rest"] = rest;
      } catch {
        // Ignore: some proxied clients may be read-only; we still have `rest` local.
      }
    }

    const fromOriginal = rest["from"].bind(rest);
    rest["from"] = (tabela: string) =>
      usandoBancoExterno() ? fromExterno(tabela) : fromOriginal(tabela);

    const rpcOriginal = rest["rpc"].bind(rest);
    rest["rpc"] = (nome: string, argumentos?: Record<string, unknown>, opcoes?: unknown) =>
      usandoBancoExterno() ? rpcExterno(nome, argumentos) : rpcOriginal(nome, argumentos, opcoes);

    // Arquivos (storage): adapta chamadas para o backend do banco externo.
    const storage = cliente["storage"] as Record<string, any>;
    if (storage) {
      const storageFromOriginal = storage["from"].bind(storage);
      storage["from"] = (balde: string) => {
        if (!usandoBancoExterno()) return storageFromOriginal(balde);

        // Wrapper que expõe os métodos usados em telas: upload, remove, download, getPublicUrl
        // Implementação cliente: delega a funções do servidor via createServerFn (server-side handlers).
        const api = {
          from: (b: string) => api,
          async upload(caminho: string, arquivo: any, opcoes?: any) {
            try {
              // arquivo pode ser Blob/File no navegador
              let contentBase64 = "";
              let contentType = "application/octet-stream";
              if (arquivo && typeof (arquivo as Blob).arrayBuffer === "function") {
                const ab = await (arquivo as Blob).arrayBuffer();
                const u8 = new Uint8Array(ab);
                contentBase64 = btoa(String.fromCharCode(...u8));
                contentType = (arquivo as any).type || contentType;
              } else if (typeof arquivo === "string") {
                contentBase64 = arquivo; // assume already base64
              }
              const { enviarArquivo } = await import("@/lib/storage.functions");
              return await enviarArquivo({
                data: {
                  balde: balde,
                  caminho,
                  conteudo_b64: contentBase64,
                  content_type: contentType,
                },
              } as any);
            } catch (e) {
              return { error: { message: String((e as Error).message ?? e) } };
            }
          },
          async remove(paths: string[] | string) {
            const lista = Array.isArray(paths) ? paths : [paths];
            const { removerArquivo } = await import("@/lib/storage.functions");
            return await removerArquivo({ data: { balde: balde, caminhos: lista } } as any);
          },
          async download(caminho: string) {
            const { baixarArquivo } = await import("@/lib/storage.functions");
            return await baixarArquivo({ data: { balde: balde, caminho } } as any);
          },
          getPublicUrl(caminho: string) {
            // Serve um caminho público relativo; o servidor pode expor /storage/*
            return { publicURL: `/storage/${balde}/${caminho}` };
          },
        };
        return api;
      };
    }

    void sincronizarBancoAtual();
  })();
}
